import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'

// Lấy hoặc tạo device_token cố định cho máy này
// Dùng fallback cho iOS < 14.5 không có crypto.randomUUID()
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

function getDeviceToken() {
  const key = 'tchat_device_token'
  let token = localStorage.getItem(key)
  if (!token) {
    token = generateUUID()
    localStorage.setItem(key, token)
  }
  return token
}

function getDeviceName() {
  const ua = navigator.userAgent
  if (/iPhone|iPad/.test(ua)) return 'iPhone/iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac/.test(ua)) return 'Mac'
  return 'Trình duyệt web'
}

export default function LoginPage() {
  const { login, register, verifyOtp } = useAuth()
  const [mode, setMode] = useState('login') // 'login' | 'register' | 'otp'
  const [form, setForm] = useState({ login: '', password: '', username: '' })
  const [showPwd, setShowPwd] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [pending, setPending] = useState(false)
  // Lưu lại thông tin khi cần OTP
  const [pendingLogin, setPendingLogin] = useState(null)

  function onChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }))
  }

  async function onSubmit(e) {
    e.preventDefault()
    setError(''); setLoading(true); setPending(false)
    try {
      if (mode === 'login') {
        const deviceToken = getDeviceToken()
        const deviceName = getDeviceName()
        const result = await login(form.login, form.password, deviceToken, deviceName)
        if (result?.needs_otp) {
          setPendingLogin({ login: form.login, password: form.password, deviceToken, deviceName })
          setMode('otp')
        }
      } else {
        const result = await register(form.username, form.password)
        if (result.pending) {
          setPending(true)
          setForm({ login: '', password: '', username: '' })
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function onSubmitOtp(e) {
    e.preventDefault()
    setError(''); setLoading(true)
    try {
      await verifyOtp(pendingLogin.login, pendingLogin.password, pendingLogin.deviceToken, pendingLogin.deviceName, otpCode)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (pending) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.title}>💬 T-chat</h2>
          <div style={styles.pendingBox}>
            <div style={styles.pendingIcon}>⏳</div>
            <p style={styles.pendingTitle}>Đăng ký thành công!</p>
            <p style={styles.pendingText}>
              Tài khoản đang chờ quản trị viên kích hoạt.
              Bạn sẽ có thể đăng nhập sau khi được duyệt.
            </p>
            <button style={styles.btn} onClick={() => { setPending(false); setMode('login') }}>
              Quay lại đăng nhập
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Màn hình nhập OTP
  if (mode === 'otp') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.title}>💬 T-chat</h2>
          <div style={styles.otpBox}>
            <div style={styles.otpIcon}>🔐</div>
            <p style={styles.otpTitle}>Xác nhận thiết bị mới</p>
            <p style={styles.otpText}>
              Đây là lần đầu đăng nhập từ thiết bị này.<br />
              Admin đã nhận được mã OTP — liên hệ admin để lấy mã.
            </p>
            <form onSubmit={onSubmitOtp} style={styles.form}>
              <input
                placeholder="Nhập mã OTP 6 số"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value)}
                style={{ ...styles.input, textAlign: 'center', fontSize: 22, letterSpacing: 8 }}
                maxLength={6}
                autoFocus
                required
              />
              {error && <p style={styles.error}>{error}</p>}
              <button type="submit" style={styles.btn} disabled={loading}>
                {loading ? 'Đang xác nhận...' : 'Xác nhận'}
              </button>
              <button type="button" style={styles.btnGhost} onClick={() => { setMode('login'); setError('') }}>
                Quay lại
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>💬 T-chat</h2>

        <div style={styles.tabs}>
          <button style={mode === 'login' ? styles.tabActive : styles.tab} onClick={() => { setMode('login'); setError('') }}>Đăng nhập</button>
          <button style={mode === 'register' ? styles.tabActive : styles.tab} onClick={() => { setMode('register'); setError('') }}>Đăng ký</button>
        </div>

        <form onSubmit={onSubmit} style={styles.form}>
          {mode === 'register' && (
            <input name="username" placeholder="Tên đăng nhập / bí danh (≥3 ký tự)" value={form.username} onChange={onChange} style={styles.input} required />
          )}

          {mode === 'login' && (
            <input name="login" placeholder="Tên đăng nhập" value={form.login} onChange={onChange} style={styles.input} required />
          )}

          <div style={{ position: 'relative' }}>
            <input name="password" type={showPwd ? 'text' : 'password'} placeholder="Mật khẩu" value={form.password} onChange={onChange} style={{ ...styles.input, paddingRight: 40, width: '100%', boxSizing: 'border-box' }} required />
            <button type="button" onClick={() => setShowPwd(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#888', fontSize: 18 }}>
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? 'Đang xử lý...' : mode === 'login' ? 'Đăng nhập' : 'Đăng ký'}
          </button>
        </form>
      </div>
    </div>
  )
}

const styles = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', padding: '16px' },
  card: { background: '#fff', borderRadius: 12, padding: '32px 28px', width: '100%', maxWidth: 360, boxShadow: '0 2px 16px rgba(0,0,0,0.1)', boxSizing: 'border-box' },
  title: { textAlign: 'center', marginBottom: 24, color: '#1a1a2e' },
  tabs: { display: 'flex', marginBottom: 20 },
  tab: { flex: 1, padding: '8px 0', border: 'none', background: '#f0f2f5', cursor: 'pointer', borderRadius: 6, margin: '0 4px' },
  tabActive: { flex: 1, padding: '8px 0', border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer', borderRadius: 6, margin: '0 4px', fontWeight: 600 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  input: { padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box' },
  btn: { padding: '12px 0', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%' },
  btnGhost: { padding: '10px 0', background: 'none', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, cursor: 'pointer', width: '100%' },
  error: { color: '#e53e3e', fontSize: 13, margin: 0 },
  pendingBox: { textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  pendingIcon: { fontSize: 48 },
  pendingTitle: { fontWeight: 700, fontSize: 16, color: '#1a1a2e', margin: 0 },
  pendingText: { color: '#6b7280', fontSize: 14, lineHeight: 1.6, margin: 0 },
  otpBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' },
  otpIcon: { fontSize: 48 },
  otpTitle: { fontWeight: 700, fontSize: 16, color: '#1a1a2e', margin: 0 },
  otpText: { color: '#6b7280', fontSize: 13, lineHeight: 1.6, margin: 0 },
}
